<template>
	<div id="device-info-table">
		<div>
      <v-row class="pl-4 pt-4">
        <v-col cols="2">
          <v-autocomplete
            v-model="selectedOrg"
            :items="orgName"
            label="Organisations"
            item-text="name"
            outlined
            @change="fetchOrgNodes"
          />
        </v-col>

				<v-col cols="2">
					<v-autocomplete
						v-model="selectedGroup"
						:items="groups"
						item-text="name"
						item-value="id"
						label="Group"
						:loading="loadingOrg"
						outlined
					/>
				</v-col>

        <v-col cols="2" class="pt-8">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
				<v-spacer></v-spacer>
				<v-col cols="2" class="d-flex justify-end pr-6">
					<v-icon 
						@click="reload(paginationInfo.pageNum, paginationInfo.rowsPerPage)"
					>
						mdi-refresh
					</v-icon>
					<!-- exportReport -->
					<v-icon @click="displayDownloadOption = true">
						mdi-download
					</v-icon>
				</v-col>
      </v-row>
    </div>

		<v-data-table
			:headers="headers"
			:items="tableData"
			:loading="loading"
			:hide-default-footer="true"
			:items-per-page="-1"
			class="elevation-1 admin-table"
		>

			<template v-slot:item.vehicle_name="{ item }">
				<div @click.stop="rowClicked(item)">
					{{ item.vehicle_name }}
				</div>
			</template>

			<template v-slot:item.install_tag="{ item }">
				<div @click.stop="rowClicked(item)">
					{{ item.install_tag }}
				</div>
			</template>

			<template v-slot:item.firmware_version="{ item }">
				<div @click.stop="rowClicked(item)">
					{{ item.firmware_version }}
				</div>
			</template>

			<template v-slot:item.io_pins="{ item }">
				<span v-for="(io, index) in item.io_pins" :key="index" class="display-block" @click.stop="rowClicked(item)">
					{{ io }}
				</span>
			</template>

			<template v-slot:item.tz="{ item }">
				<div @click.stop="rowClicked(item)">
					{{ item.tz }}
				</div>
			</template>

			<template v-slot:item.dst_offset="{ item }">
				<div @click.stop="rowClicked(item)">
					{{ item.dst_offset }}
				</div>
			</template>
			<template v-slot:item.device_imei="{ item }">
				<span class="display-block" @click.stop="rowClicked(item)"> {{ item.device_imei }} </span>
				<span class="display-block" @click.stop="rowClicked(item)"> {{ item.device_iccid }} </span>
			</template>
			
			<template v-slot:item.device_model="{ item }">
				<v-tooltip bottom>
					<template v-slot:activator="{ on, attrs }">
						<span
							class="display-block"
							v-bind="attrs"
							v-on="on"
							@click.stop="rowClicked(item)"
						>
							{{ mapDeviceName(item.device_model) }}
						</span>
					</template>
					<span>{{ mapDeviceNameToolTip(item.device_model) }}</span>
				</v-tooltip>
			</template>
	
			<template v-slot:item.cameras="{ item }">
				<span v-for="(cam, index) in item.cameras" :key="index" class="display-block cam" :class="{ 'active-cam': cam.state }" @click.stop="rowClicked(item)">
					{{ cam.name }}
				</span>
			</template>
			<template v-slot:progress>
				<v-progress-linear color="blue" height="2" indeterminate></v-progress-linear>
			</template>

			<template v-slot:item.actions="{item}">
        <div class="reports-action-launch" @click="launchRowAction">
          <v-icon>mdi-dots-vertical</v-icon>
          <ActionContainer :ref="item.key" :heading="'Action'" class="report-action-options">
            <ActionItem
              label="Device Details"
              icon="mdi-laptop"
							@click.native="rowClicked(item)"
            />
            <ShowOnMap :org="orgId" :device="item.id" />
          </ActionContainer>
        </div>
      </template>
	
		</v-data-table>

		<DeviceInfo 
			v-if="displayDeviceInfo"
      :display-device-info="displayDeviceInfo"
      :device-id="selectedDeviceId"
			:vehicle-name="vehicleName"
      :org="orgId"
      @close="displayDeviceInfo = false"
    />

		<Pagination
      :pagination-info="paginationInfo"
			:show-row-option="true"
      @updatedPage="reload"
    />

		<Dialog
			:display="displayDownloadOption"
			:title="'Download Device Information Report'"
			:width="500"
			:icon="'mdi-download'"
			@close="displayDownloadOption = false"
		>
			<div>
				<div>
					<v-radio-group v-model="downloadOption">
						<v-radio label="Export current page" value="current"></v-radio>
						<v-radio v-if="paginationInfo.pageCount && paginationInfo.pageCount  > 1" :label="downnloadFullLabel" value="full"></v-radio>
					</v-radio-group>

					<div v-if="loading && downloadProgress" style="padding: 10px">
						<v-progress-linear v-model="downloadProgress" striped :height="15"></v-progress-linear>
					</div>
				</div>

				<div style="display: flex; justify-content: space-between;">
					<v-btn text @click="displayDownloadOption = false">Cancel</v-btn>
					<v-btn text @click="downloadReport">download</v-btn>
				</div>
			</div>
		</Dialog>
	</div>
</template>

<script>
	import { mapState, mapActions, mapMutations } from "vuex";

	import {
		GetOrgTree,
		getOrgDeviceInformation,
		GetOrgDevicesByNode,
		SearchOrgDevices
	} from "@/services/API.js";

	import { formatTZOffset } from "@/utils/index"

	import { presence } from '@/mixins/presence'

	import moment from "moment";
	import xlsx from 'xlsx';

	import Pagination from "@/components/general/Pagination.vue"
	import DeviceInfo from '@/components/deviceInfo/deviceInfo.vue';
	import ActionContainer from '@/components/general/ActionContainer.vue';
	import ActionItem from '@/components/general/ActionItem.vue';
	import ShowOnMap from '@/components/general/menu/ShowOnMap.vue'
	import Dialog from '@/components/general/Dialog.vue';


	import { Logger } from "aws-amplify";
	const logger = new Logger("device information");

	export default {
    name: 'DeviceInformation',
		components: {
			Pagination,
			DeviceInfo,
			ActionContainer,
			ActionItem,
			ShowOnMap,
			Dialog
		},
		mixins: [ presence ],
		props: {
			update: {
				type: Number,
				default: 0
			}
		},
		data: () => ({
			displayDownloadOption: false,
			downloadOption: 'current',
			search: '',
			headers: [
				{
          text: 'Vehicle',
          value: 'vehicle_name',
          align: 'start'
        },
				{
					text: 'Install Tag',
					value: 'install_tag',
					align: 'start'
				},
       	{
          text: 'Model',
          value: 'device_model',
          align: 'start'
        },
        {
          text: 'Firmware',
          value: 'firmware_version',
          align: 'start'
        },
        {
          text: 'IMEI/ICCID',
          value: 'device_imei',
          align: 'start'
        },
        {
          text: 'Cameras',
          value: 'cameras',
          align: 'start'
        },
        {
          text: 'I/O',
          value: 'io_pins',
          align: 'start'
        },
        {
          text: 'TZ',
          value: 'tz',
          align: 'start'
        },
				{
          text: 'DST',
          value: 'dst_offset',
          align: 'start'
        },
				{
          text: 'Actions',
          value: 'actions',
          align: 'left'
        }
      ],
			loading: false,
			date: moment().format("YYYY-MM-DD HH:mm:ss"),
			devices: [],
			deviceInfo: [],
      selectedOrg: '',
			tableData: [],

			selectedGroup: '',
			groups: [],

			paginationInfo: {
        pageNum: 1,
        pageCount: 1,
        rowsPerPage: 20
      },

			page: 1,

			loadingOrg: false,
			downloadProgress: 0,

			searchTermTimeout: false,
      lastSearchAbove3Characters: false, 

			displayDeviceInfo: false,
			selectedDeviceId: '',
			vehicleName: ''
		}),
		computed: {
			...mapState('devicePresence', [
				'vehicleStatus', 'devicesMap'
			]),
			...mapState([
				'orgs'
			]),

      orgName() {
      	const orgs = this.orgs.map((org) => ({
					name: org.name
				}));
 
        return orgs;
      },
      orgId() {
        return this.retrunOrgID(this.selectedOrg);
      },
			deviceIds() {
				return this.devices.map(device => device.id);
			},
			downnloadFullLabel() {
				return `Export all ${this.paginationInfo.pageCount} pages`
			}
		},
		watch: {
			selectedGroup() {
				this.reload(1, this.paginationInfo.rowsPerPage)
			},

			search(val) {
				if (this.searchTermTimeout) {
					clearTimeout(this.searchTermTimeout)
				}
				
				// if our string is above two characters, lets set a timeout to load the vehicles with the new search term
				if(val.length > 2) {
					this.searchTermTimeout = setTimeout(() => {
						this.searchVehicles()
						this.lastSearchAbove3Characters = true
						this.searchTermTimeout = false
					}, 1500)
				
				// if the user has dropped below 3 characters in the search, we have to search without a search term
				} else if(this.lastSearchAbove3Characters) {
						this.searchTermTimeout = setTimeout(() => {
						this.searchVehicles()
						this.lastSearchAbove3Characters = false
						this.searchTermTimeout = false
					}, 1500)
				}
			}
		},
		mounted() {
			window.EventBus.$on("wcToggleContacts", this.handleToggleContacts);
			window.EventBus.$on("showSelectContacts", this.handleContactSelection);
			window.EventBus.$on("refreshData", this.refreshData);
			
			this.getOrgs();
		},
		methods: {
			...mapMutations('presence', ['setOrgToWatch']),
			downloadReport () {
				if(this.downloadOption == 'current') {
					this.downloadPages([this.paginationInfo.pageNum])
				}

				else {
					// creates an array of pages based on the current pagecount [1,2,3,4] etc
					let pageList = new Array(this.paginationInfo.pageCount).fill(0).map((i, index) => index+1)
					this.downloadPages(pageList)
				}
			},

			mapDeviceName(model) {
				const map = {
					'CAMERAMATICS_D700-SD-4G': 'Genie',
					'CAMERAMATICS_JH4_NT-HD-4G': 'Drive 360 4CH',
					'CAMERAMATICS_JH8_NT-HD-4G': 'Drive 360 8CH',
					'CAMERAMATICS_JH8-HD-4G': 'Legacy 8CH',
					'CAMERAMATICS_JI7A-SD-4G': 'Genie Pro',
					'CAMERAMATICS_JH4-HD-4G': 'Legacy 4CH',
				}

				return map.hasOwnProperty(model) ? map[model] : model
			},

			mapDeviceNameToolTip(model) {
				const map = {
					'Genie Pro': 'CAMERAMATICS_JI7A-SD-4G',
					'Genie': 'CAMERAMATICS_D700-SD-4G',
					'Drive 360 4CH': 'CAMERAMATICS_JH4_NT-HD-4G',
					'Drive 360 8CH': 'CAMERAMATICS_JH8_NT-HD-4G',
					'Legacy 8CH': 'CAMERAMATICS_JH8-HD-4G',
					'Legacy 4CH': 'CAMERAMATICS_JH4-HD-4G'
				}

				return map.hasOwnProperty(model) ? map[model] : model
			},

			rowClicked(item) {
				logger.info('rowClicked')
				this.selectedDeviceId = item.id
				this.vehicleName = `${item.vehicle_name} (${item.install_tag})`
				this.displayDeviceInfo = true
			},

			mapIoValues(str) {
        const map = {
          'l': 'Low',
          'h': 'High',
          'true': 'Yes',
          'false': 'No',
          'link_to_cam': 'Link to camera',
          'disable_cam': 'Disable cameras'
        }
        return map.hasOwnProperty(str) ? map[str] : str
      },

			async searchVehicles() {
				this.loading = true
				this.devices = []

				const search = await SearchOrgDevices(this.orgId, this.selectedGroup, this.search, this.paginationInfo.pageNum, this.paginationInfo.rowsPerPage)

				if(Object.hasOwn(search, 'Devices') && Object.hasOwn(search, 'page_num') && Object.hasOwn(search, 'total_pages')) {
					this.devices = search.Devices;
					this.paginationInfo.pageNum = search.page_num;
					this.paginationInfo.pageCount = search.total_pages;

					await this.batchGetDeviceInfomation(this.devices.map(device => device.id))
				}
				
				this.mapTableData();
				this.loading = false;

				logger.info('search...', search)
			},


			mapTableData(deviceInfo = false, devicesList = false) {
				// if we are passing a list, we want to map that instead! we may be using this to map the pages for a download.
				let devices = deviceInfo || this.deviceInfo

				logger.debug("mapTableData", deviceInfo, this.deviceInfo, devices)

				const mappedInfo = devices.map(item => (
					{
						vehicle_name: this.returnVehicleName(item, devicesList),
						install_tag: this.returnInstallTag(item, devicesList),
						firmware_version: item.firmwareVersion,
						device_model: item.model,
						device_imei: this.returnDeviceComponentInfo(item.components, 'modem') ? this.returnDeviceComponentInfo(item.components, 'modem')['imei'] : '',
						device_iccid: this.returnDeviceComponentInfo(item.components, 'modem') ? this.returnDeviceComponentInfo(item.components, 'modem')['iccid'] : '',
						cameras: this.mapCameraInfo(this.returnDeviceComponentInfo(item.components, 'camera'), (devicesList && devicesList.length)),
						io_pins: this.mapIOInfo(this.returnDeviceComponentInfo(item.components, 'io')),
						tz: formatTZOffset(item.time.tzo),
						dst_offset: formatTZOffset(item.time.dsto),
						id: item.id
					}
				))

				// if we passed a list of devices, we want to return the mapped response as we may be building the export (:
				if(deviceInfo) {
					return mappedInfo
				}

				// if this is just being used to load a page of data, we just set the tableData property as normal!
				else {
					this.tableData = mappedInfo
				}
			},	

			returnDeviceComponentInfo(data, type) {
				if(!data || !type ) return [];
				return data.find(item => item.type == type);
			},

			mapCameraInfo(cameras, text = false) {
				logger.debug("map cameras", cameras)
				if (!cameras || !cameras.hasOwnProperty('channels')) return;
				let info = [];
				let cameraCount = cameras.channels.length;

				cameras.channels.forEach((channel, index) => {
					let state = JSON.parse(channel.enabled.toLowerCase()); 
					let channelInfo = {
						state: state,
						name: cameraCount > index + 1 ? `${channel.name},` : channel.name
					}

					info.push(channelInfo);
				});

				// for the export, we want this as text, not an object
				return text
					? info.map(i => i.name).filter(i=>i && i.length).join(', ')
					: info;
			},

			mapIOInfo(io) {
				if (!io || !io.hasOwnProperty('pins')) return;
				let info = [];
				let pinCount = io.pins.length;

				io.pins.forEach((pin, index) => {
					let pinName = pinCount > index + 1 ? `${pin.name},` : pin.name;

					info.push(pinName);
				});

				return info;
			},

			mapStorageInfo(storage) {
				if (!storage || !storage.hasOwnProperty('devices')) return;
				let info = [];

				storage.devices.forEach(device => {
					let used = device.capacity.split('/')[0];
					let capacity = device.capacity.split('/')[1];
					const map = {
						'hdd': 'HDD',
						'sd': 'SD'
					};
					let storageInfo = `${map[device.medium]}, ${used} MB, ${capacity} MB \n`

					info.push(storageInfo);
				});

				return info;
			},

			...mapActions(["getOrgs"]),
			
      retrunOrgID(name) {
        let org = this.orgs.find(org => {
          return org.name == name;
        });

        return org ? org.id : '';
      },	

			async fetchOrgNodes() {
        this.loadingOrg = true;
				this.devices = [];
				this.deviceInfo = [];
				this.tableData = [];
    		
				const orgTree = await GetOrgTree(this.orgId);
						
				if (orgTree) {
					this.nodes = orgTree;
					this.groups = this.flattenOrgTree([this.nodes.root_node], true)

					if(this.groups.length > 0) {
						this.selectedGroup = this.groups[0].id
					}

					this.loadingOrg = false;
				}

				this.loadingOrg = false;
			},

			flattenOrgTree(node, returnObject = false) {
				var result = [];
				
				node.forEach((node) => {
					if(returnObject) {
						logger.info('node', node)
						result.push({
							name: node.metadata.label,
							id: node.id
						});
					}
					else {
						logger.info('node', node)
						result.push(node.metadata.label);
					}
						
					if (Array.isArray(node.nodes)) {
						result = result.concat(this.flattenOrgTree(node.nodes, returnObject));
					}
				});
				return result;
			},

			/**
			 * downloads an array of pages
			 * @param pages - an array of the pages to download, provided as an array e.g [1, 2, 3]
			 */
			async downloadPages (pages = [1]) {
				if(!this.orgId || !this.selectedGroup) 
					return

				this.loading = true
				this.downloadProgress = 5 // set it to 5 just so the bar is visible
				let segmentsComplete = 0
				const maxDownloadProgress = (pages.length * 3) || 1 // we have 3 points we increment the value

				logger.debug("pages to download", pages)

				// function to update the loading bar
				const updateProgress = () => {
						segmentsComplete++
						this.downloadProgress = Math.ceil((segmentsComplete / maxDownloadProgress) * 100)
				}
				
				const requests = pages.map(pageToDownload => new Promise(async resolve => {
					try {
						let devices = await GetOrgDevicesByNode(this.orgId, this.selectedGroup, pageToDownload, this.paginationInfo.rowsPerPage)
						updateProgress()

						let deviceInfo = await this.batchGetDeviceInfomation(devices.Devices.map(device => device.id), true)
						updateProgress()

						logger.debug("download pages", pageToDownload, devices, deviceInfo)

						//let mappedData = this.mapTableData(deviceInfo, devices.Devices)
						updateProgress()
						resolve({
							devices: devices.Devices,
							info: deviceInfo
						})
					} catch (err) {
						resolve({
							devices: [],
							info: []
						})
					}
				}))

				const dataForDownload = await Promise.all(requests) 

				logger.debug("download info 1", dataForDownload)

				let deviceData = []
				let infoData = []
				// reduce pages

				for(let x = 0; x < dataForDownload.length; x++) {
					let record = dataForDownload[x]
					if(record.devices && Array.isArray(record.devices)) {
						deviceData = [...deviceData, ...record.devices]
					}

					if(record.info && Array.isArray(record.info)) {
						infoData = [...infoData, ...record.info]
					}
				}

				logger.debug("download info 2", deviceData, infoData)
				
				//let allPages = dataForDownload.flat()

				//logger.debug("download info 2", allPages)

				this.exportReport(infoData, deviceData)

				await (() => new Promise(res => {
					setTimeout(() => {
						res()
					}, 750)
				}))()

				this.loading = false
				this.displayDownloadOption = false
			},
			
			// this is called when the pagination control is updated
			async reload(page = 1, rows = 20) {
				
				if(!this.orgId || !this.selectedGroup) return
				
				this.loading = true;
				this.devices = [];
				this.deviceInfo = [];
				this.tableData = [];

				try {
					let devices = await GetOrgDevicesByNode(this.orgId, this.selectedGroup, page, rows);
					let vehicles = {}
					
					if(devices.Devices) {
						let vehiclesToRequest = devices.Devices.map(i => i.trackable_id).filter(i => i)
						vehicles = {
							Vehicles: await this.$loadVehicles(this.orgId, vehiclesToRequest)
						}
						logger.debug("vehicles fetched", vehicles.Vehicles)
						this.devices = devices.Devices;
						this.paginationInfo.pageNum = devices.page_num;
						this.paginationInfo.pageCount = devices.total_pages;
						this.paginationInfo.rowsPerPage = rows

						await this.batchGetDeviceInfomation(this.devices.map(device => device.id))
					}
					else {
						throw new Error('An error occurred')
					}

											
					this.mapTableData();
					this.loading = false;
				}
				catch {
					this.loading = false
				}
				
			},

			// takes an array of device ids. when downloading the export, we use returnDevices to get the response :)
			async batchGetDeviceInfomation(devices, returnDevices = false) {
				// Split devices into arrays of max 100
				const deviceInfoSets = [];
				for (let i = 0; i < devices.length; i += 100) {
					deviceInfoSets.push(devices.slice(i, i + 100))
				}
				
				// after we split our devices ids into sets, we can batch request the device info from the API 
				return new Promise(async resolve => {
					// if we had no devices we just return here
					if(!devices.length) {
						resolve(true)
					}

					//map our sets of device ids into promises
					let infoRequests = deviceInfoSets.map(deviceIdList => new Promise(async res => {
						getOrgDeviceInformation(this.orgId, deviceIdList).then(deviceInfo => {
							if (deviceInfo.Devices) {
								res(deviceInfo.Devices)
							} else {
								res([])
							}
						})		
					}))

					let deviceInfo = await Promise.all(infoRequests)
					deviceInfo = deviceInfo.flat()

					if(returnDevices) {
						return resolve(deviceInfo)
					} 
					
					else {
						this.deviceInfo = deviceInfo
					}
					
					resolve(true)
				})
			},

			refreshData(data) {
				// Array of vehicle IDs that are updated
				let vehicleIds = data.IDs;

				// Find the updated vehicles in data table and map new vals to row item directly
				for (var i in this.vehicles) {
					for (var j in vehicleIds) {
						if (this.vehicles[i].id === vehicleIds[j]) {
							for (const [key, value] of Object.entries(data)) {
								// eslint-disable-next-line no-prototype-builtins
								if (this.vehicles[i].hasOwnProperty(key)) {
									this.vehicles[i][key] = value;
								}
							}
						}
					}
				}
			},

			returnInstallTag(data, devices = false) {
				let devicesList = devices || this.devices
				const installTag = devicesList.find(item => item.id == data.id);

				return installTag ? installTag.install_tag : '';
			},

			returnVehicleName(data, devices = false) {
				let devicesList = devices || this.devices
				const vehicle = devicesList.find(item => item.id == data.id);

				logger.debug("returnVehicleName", this.devices, devices)

				return vehicle ? vehicle.vehicle : '';
			},

			mapAudioSettings(str) {
        const map = {
          'tone': 'Tone only',
          'voice': 'Voice only',
          'toneVoice': 'Tone and voice'
        }

        return map.hasOwnProperty(str) ? map[str] : str
      },

			formatDstRule(rule) {
        const days = [
          "Sunday", "Monday", "Tuesday", "Wednesday",
          "Thursday", "Friday", "Saturday"
        ];

        const months = [
          "January", "February", "March", "April",
          "May", "June", "July", "August",
          "September", "October", "November", "December"
        ];

        const occurrenceMap = {
          first: "First",
          second: "Second",
          third: "Third",
          fourth: "Fourth",
          last: "Last"
        };

        const hour = parseInt(rule.hour, 10);

        // Convert to 12-hour format
        const period = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;

        return `${occurrenceMap[rule.occurrence]} ${days[rule.day]} of ${months[rule.month - 1]} at ${hour12}:00 ${period}`;
      },

      formatOffset(offsetStr) {
        const minutes = parseInt(offsetStr, 10);

        if (isNaN(minutes) || minutes === 0) {
          return "0 minutes";
        }

        const sign = minutes > 0 ? "+" : "-";
        const absMinutes = Math.abs(minutes);

        const hours = Math.floor(absMinutes / 60);
        const remainingMinutes = absMinutes % 60;

        let parts = [];

        if (hours > 0) {
          parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
        }

        if (remainingMinutes > 0) {
          parts.push(`${remainingMinutes} minute${remainingMinutes !== 1 ? "s" : ""}`);
        }

        return `${sign}${parts.join(" ")}`;
      },

      formatTime24(rebootTime) {
        const totalMinutes = parseInt(rebootTime, 10);

        if (isNaN(totalMinutes) || totalMinutes < 0) {
          return "Invalid time";
        }

        const hours = Math.floor(totalMinutes / 60) % 24;
        const minutes = totalMinutes % 60;

        const pad = (n) => String(n).padStart(2, "0");

        return `${pad(hours)}:${pad(minutes)}`;
      },

			exportReport(fullDeviceInfoList = false, fullDeviceList = false) {
				const SHEET_HEADERS = {
					General: [
						"Vehicle",
						"Install Tag",
						"Manufacturer",
						"Model","Revision",
						"Serial","Firmware"
					],
					Modem: [
						"Vehicle",
						"Install Tag",
						"Manufacturer",
						"Model",
						"Revision",
						"IMEI",
						"ICCID",
						"APN"
					],
					MCU: [
						"Vehicle",
						"Install Tag",
						"Firmware Version"
					],
					Storage: [
						"Vehicle",
						"Install Tag",
						"Class",
						"Medium",
						"Model",
						"Used (MB)",
						"Total Capacity (MB)"
					],
					Camera: [
						"Vehicle",
						"Install Tag",
						"Channel",
						"Name",
						"Enabled",
						"Mirror",
						"Flip",
						"Main Quality",
						"Sub Quality",
						"Audio",
						"Stream Enabled",
						"Disable on ACC"
					],
					Inputs: [
						"Vehicle",
						"Install Tag",
						"Input",
						"Name",
						"Active",
						"Alert",
						"Guard",
						"Action",
						"Action Context"
					],
					"ADAS Config": [
						"Vehicle",
						"Install Tag",
						"Camera Channel",
						"Camera to Ground (mm)",
						"Camera to Centre (mm)",
						"Camera to Front (mm)",
						"Vehicle Width (mm)",
						"FCW Min Speed (kph)",
						"FCW Collision Time (s)",
						"PCW Min Speed (kph)",
						"PCW Max Speed (kph)",
						"LDW Min Speed (kph)",
						"LDW Crossing Distance (mm)",
						"FPW Min Speed (kph)", 
						"FPW Collision Time (s)"
					],
					"ADAS Alerts": [
						"Vehicle", 
						"Install Tag", 
						"FCW Enabled",
						"FCW Rate Limit",
						"FPW Enabled",
						"FPW Rate Limit",
						"LDW Enabled",
						"LDW Rate Limit",
						"FPW Enabled",
						"FPW Rate Limit",
					],
					"DMS Config": [
						"Vehicle", 
						"Install Tag", 
						"Channel",
						"ROI",
						"Driver Position",
						"Driver Left Angle",
						"Driver Right Angle",
						"Driver Down Angle"
					],
					"DMS Alerts": [
						"Vehicle",
						"Install Tag", 
						"Phone Enabled",
						"Phone Speed",
						"Phone Duration",
						"Phone Rate Limit",
						"Eyes Closed Enabled",
						"Eyes Closed Speed",
						"Eyes Closed Duration",
						"Eyes Closed Rate Limit",
						"Yawn Enabled",
						"Yawn Speed",
						"Yawn Duration",
						"Yawn Rate Limit",
						"Smoking Enabled",
						"Smoking Speed",
						"Smoking Duration",
						"Smoking Rate Limit",
						"Distract L Enabled",
						"Distract L Speed",
						"Distract L Duration",
						"Distract L Rate Limit",
						"Distract R Enabled",
						"Distract R Speed",
						"Distract R Duration",
						"Distract R Rate Limit",
						"Distract D Enabled",
						"Distract D Speed",
						"Distract D Duration",
						"Distract D Rate Limit",
						"Occlusion Enabled"
					],
					Audio: [
						"Vehicle",
						"Install Tag",
						"Volume",
						"DMS Warnings",
						"ADAS Warnings"
					],
					IMU: [
						"Vehicle",
						"Install Tag",
						"Vehicle Type",
						"Alerts Enabled"
					],
					Power: [
						"Vehicle",
						"Install Tag",
						"Power Down Delay",
						"Low Voltage",
						"Daily Reboot",
						"Reboot Time"
					],
					Recording: [
						"Vehicle",
						"Install Tag",
						"Codec"
					],
					Speeding: [
						"Vehicle",
						"Install Tag",
						"Alerts Enabled"
					],
					Time: [
						"Vehicle",
						"Install Tag",
						"TZO (min)",
						"DST Enabled",
						"DST Offset (min)",
						"DST Start Week",
						"DST Start Day of Week",
						"DST Start Time of Day",
						"DST End Week",
						"DST End Day of Week",
						"DST End Time of Day"
					]
				}

				function yesNo(v){
					if(v === true || v === "true") return "YES"
					if(v === false || v === "false") return "NO"
					return v || ""
				}

				function onOff(v){
					if(v === true || v === "true") return "ON"
					if(v === false || v === "false") return "OFF"
					return v || ""
				}

				function splitCapacity(cap){
					if(!cap) return ["",""]
					const [used,total] = cap.split("/")
					return [Number(total), Number(used)]
				}

				function getComponent(device,type){
					return device.components?.find(c=>c.type===type)
				}

				function getAlert(alerts,type){
					return alerts?.find(a=>a.type===type) || {}
				}

				function writeSheet(wb, name, rows){
					if(!rows.length) return

					const header = SHEET_HEADERS[name] || []

					const ws = xlsx.utils.aoa_to_sheet([
						header,
						...rows
					])

					xlsx.utils.book_append_sheet(wb, ws, name)
				}

				const generateDeviceReport = (devices, output) => {

					const wb = xlsx.utils.book_new()

					const sheets = {
						general:[],
						modem:[],
						mcu:[],
						storage:[],
						camera:[],
						inputs:[],
						adasConfig:[],
						adasAlerts:[],
						dmsConfig:[],
						dmsAlerts:[],
						audio:[],
						imu:[],
						power:[],
						recording:[],
						speeding:[],
						time:[]
					}

					let deviceList = fullDeviceList 
						? fullDeviceList
						: this.devices

					devices.forEach(d => {
						const vehicleObj = deviceList.find(device => device.id == d.id)
						const vehicle = vehicleObj && vehicleObj.hasOwnProperty('vehicle') ? vehicleObj.vehicle : ''
						const modem = getComponent(d,"modem")
						const mcu = getComponent(d,"mcu")
						const storage = getComponent(d,"storage")
						const camera = getComponent(d,"camera")
						const io = getComponent(d,"io")
						const adas = getComponent(d,"adas")
						const dms = getComponent(d,"dms")
						const audio = getComponent(d,"audio")
						const imu = getComponent(d,"imu")
						const power = getComponent(d,"power")
						const recording = getComponent(d,"recording")
						const speeding = getComponent(d,"speeding")
						const timeCfg = getComponent(d,"timeConfig")

						// GENERAL
						sheets.general.push([
							vehicle,
							vehicleObj.install_tag,
							d.manufacturer,
							d.model,
							"N/A",
							d.serial || "N/A",
							d.firmwareVersion
						])

						// MODEM
						if(modem){
							sheets.modem.push([
								vehicle,
								vehicleObj.install_tag,
								modem.manufacturer,
								modem.model,
								modem.revision,
								modem.imei,
								modem.iccid,
								modem.apn || ""
							])
						}

						// MCU
						if(mcu){
							sheets.mcu.push([
								vehicle,
								vehicleObj.install_tag,
								mcu.fwVersion
							])
						}

						// STORAGE
						storage?.devices?.forEach(dev=>{
							const [total,used] = splitCapacity(dev.capacity)

							sheets.storage.push([
								vehicle,
								vehicleObj.install_tag,
								dev.class?.toUpperCase(),
								dev.medium?.toUpperCase(),
								dev.model,
								used,
								total
							])
						})

						// CAMERA
						if(camera && camera.channels) {
							camera.channels.forEach(ch=>{
								logger.info('streamingEnabled', ch)
								sheets.camera.push([
									vehicle,
									vehicleObj.install_tag,
									Number(ch.id),
									ch.name,
									yesNo(ch.enabled),
									yesNo(ch.mirror),
									yesNo(ch.flip),
									ch.mainQuality?.toUpperCase(),
									ch.subQuality?.toUpperCase(),
									onOff(ch.audioEnabled),
									yesNo(ch.streamEnabled),
									yesNo(ch.disableAccOff)
								])
							})
						}

						// INPUTS
						io?.pins?.forEach(pin=>{
							sheets.inputs.push([
								vehicle,
								vehicleObj.install_tag,
								Number(pin.id),
								pin.name,
								pin.trigger === "h" ? "HIGH" : "LOW",
								yesNo(pin.alert),
								pin.guard,
								pin.hasOwnProperty('action') && pin.action.type ? this.mapIoValues(pin.action.type) : 'None',
								pin.hasOwnProperty('action') && pin.action.type == 'link_to_cam' ? pin.action.context.cameras : ''
							])
						})

						// ADAS CONFIG
						if(adas){
							const c = adas.config || {}

							sheets.adasConfig.push([
								vehicle,
								vehicleObj.install_tag,
								adas.channel,
								c.camToGround,
								c.camToCentre,
								c.camToFront,
								c.vehicleWidth,
								c.fcwMinSpeed,
								c.fcwTimeToCollision,
								c.pcwMinSpeed,
								c.pcwMaxSpeed,
								c.ldwMinSpeed,
								c.ldwCrossDist,
								c.fpwMinSpeed,
								c.fpwTimeToCollision
							])

							const fcw = getAlert(adas.alerts,"fcw")
							const pcw = getAlert(adas.alerts,"pcw")
							const ldw = getAlert(adas.alerts,"ldw")
							const fpw = getAlert(adas.alerts,"fpw")

							sheets.adasAlerts.push([
								vehicle,
								vehicleObj.install_tag,
								yesNo(fcw.enabled),
								fcw.rate,
								yesNo(pcw.enabled),
								pcw.rate,
								yesNo(ldw.enabled),
								ldw.rate,
								yesNo(fpw.enabled),
								fpw.rate
							])
						}

						// DMS CONFIG
						if(dms){
							const c = dms.config || {}

							sheets.dmsConfig.push([
								vehicle,
								vehicleObj.install_tag,
								dms.channel,
								"",
								c.driverPos || "",
								c.distractLeftAngle,
								c.distractRightAngle,
								c.distractDownAngle
							])

							const phone = getAlert(dms.alerts,"phone")
							const eyes = getAlert(dms.alerts,"eyesClosed")
							const yawn = getAlert(dms.alerts,"yawn")
							const smoke = getAlert(dms.alerts,"smoking")
							const dl = getAlert(dms.alerts,"distractLeft")
							const dr = getAlert(dms.alerts,"distractRight")
							const dd = getAlert(dms.alerts,"distractDown")
							const occ = getAlert(dms.alerts,"occlusion")

							sheets.dmsAlerts.push([
								vehicle,
								vehicleObj.install_tag,
								yesNo(phone.enabled),phone.speed,phone.duration,phone.rate,
								yesNo(eyes.enabled),eyes.speed,eyes.duration,eyes.rate,
								yesNo(yawn.enabled),yawn.speed,yawn.duration,yawn.rate,
								yesNo(smoke.enabled),smoke.speed,smoke.duration,smoke.rate,
								yesNo(dl.enabled),dl.speed,dl.duration,dl.rate,
								yesNo(dr.enabled),dr.speed,dr.duration,dr.rate,
								yesNo(dd.enabled),dd.speed,dd.duration,dd.rate,
								yesNo(occ.enabled)
							])
						}

						// AUDIO
						if(audio){
							sheets.audio.push([
								vehicle,
								vehicleObj.install_tag,
								audio.vol,
								this.mapAudioSettings(audio.dms?.set),
								this.mapAudioSettings(audio.adas?.set)
							])
						}

						const mapImuVals = {
							0: 'Heavy Truck',
							1: 'Light Truck',
							2: 'Heavy Van',
							3: 'Light Van',
							4: 'Car',
							5: 'Bus/Coach'
						}

						// IMU
						if(imu){
							sheets.imu.push([
								vehicle,
								vehicleObj.install_tag,
								mapImuVals[imu.vehicleType],
								yesNo(imu.alertsEnabled)
							])
						}

						// POWER
						if(power){
							sheets.power.push([
								vehicle,
								vehicleObj.install_tag,
								power.pdd,
								power.lvt,
								onOff(power.dailyReboot),
								this.formatTime24(power.rebootTime)
							])
						}

						// RECORDING
						if(recording){
							sheets.recording.push([
								vehicle,
								vehicleObj.install_tag,
								recording.encoderType
							])
						}

						// SPEEDING
						if(speeding){
							sheets.speeding.push([
								vehicle,
								vehicleObj.install_tag,
								yesNo(speeding.alertsEnabled)
							])
						}

						// TIME
						if(timeCfg){
							const dst = timeCfg.dst || {}

							sheets.time.push([
								vehicle,
								vehicleObj.install_tag,
								timeCfg.tzOffset,
								yesNo(dst.enabled),
								dst.offset,
								dst.dstStart?.occurrence,
								"SUNDAY",
								this.formatTime24(dst.dstStart?.hour),
								dst.dstEnd?.occurrence,
								"SUNDAY",
								this.formatTime24(dst.dstEnd?.hour)
							])
						}

					})

					writeSheet(wb,"General",sheets.general)
					writeSheet(wb,"ADAS Config",sheets.adasConfig)
					writeSheet(wb,"ADAS Alerts",sheets.adasAlerts)
					writeSheet(wb,"Audio",sheets.audio)
					writeSheet(wb,"Camera",sheets.camera)
					writeSheet(wb,"DMS Config",sheets.dmsConfig)
					writeSheet(wb,"DMS Alerts",sheets.dmsAlerts)			
					writeSheet(wb,"IMU",sheets.imu)
					writeSheet(wb,"Inputs",sheets.inputs)
					writeSheet(wb,"MCU",sheets.mcu)
					writeSheet(wb,"Modem",sheets.modem)
					writeSheet(wb,"Power",sheets.power)
					writeSheet(wb,"Recording",sheets.recording)
					writeSheet(wb,"Speeding",sheets.speeding)
					writeSheet(wb,"Storage",sheets.storage)
					writeSheet(wb,"Time",sheets.time)

					xlsx.writeFile(wb,output)
				}

				let devicesForReport = fullDeviceInfoList 
					? fullDeviceInfoList
					: this.deviceInfo
				const reportName = `${this.selectedOrg}_device_info_${moment().format("YYYY-MM-DD_HH:mm:ss")}`;
				generateDeviceReport(devicesForReport, reportName + '.xlsx')
			},

			downloadFile (data = false) {
				let downloadData = data || this.tableData
				if (downloadData.length > 0) {
					// create our excel file
					const wb = xlsx.utils.book_new();
					
					// Filter unwanted fields from report
					let cleanedReportCollection = [];


					downloadData.forEach(item => {
						let cleanedReportItem = {};
						
						this.headers.forEach(heading => {
							let key = heading.value;
							logger.info('key ', key)
							// eslint-disable-next-line no-prototype-builtins
							if (item.hasOwnProperty(key)) {   
								logger.info('prop ', item[key])          
								item[key] ? cleanedReportItem[key] = item[key].toString() : '';
							}
		
							if(!cleanedReportCollection.includes(cleanedReportItem)) {
								cleanedReportCollection.push(cleanedReportItem);
							}
						});
					});

					// create our worksheets
					const report = xlsx.utils.json_to_sheet(cleanedReportCollection);

					// apend them to the workbook
					xlsx.utils.book_append_sheet(wb, report, 'Device Information');

					const reportName = `${this.selectedOrg}_device_information_${moment().format("YYYY-MM-DD HH:mm:ss")}`;
					xlsx.writeFile(wb, reportName + ".xlsx");
				}
			},

			launchRowAction(e) {
				let allOptions = document.querySelectorAll(".report-action-options");

				for (let option of allOptions) {
					option.classList.remove('active-options');
					if (option === e.target.nextSibling) {
						option.classList.toggle('active-options');
					} 
				}
			}
		}
	};
</script>

<style lang="scss">
	.vehicles-search-container {
    width: 50%;
    padding-left: 1em;
  }
	.reports-action-launch {
    position: relative;
    cursor: pointer;
  }
	.report-action-options {
    display: none;
  }
	.active-options {
    display: block !important;  
  }
	.middle-align {
		margin-bottom: 40px;
	}
	.info-popup-container {
		border: solid #eeeeee 2px !important;
		background-color: white !important;
		-webkit-box-shadow: 1px 2px 6px 3px rgba(0,0,0,0.26); 
		box-shadow: 1px 2px 6px 3px rgba(0,0,0,0.26);
	}
	.cam {
		color: red !important;
	}
	.active-cam {
		color: green !important;
	}
	#device-info-table {
		.v-data-table__wrapper {
			overflow-x: visible !important;
			overflow-y: visible !important;
		}
		.download-container {
			position: absolute;
    	right: 45px;
    	top: 10px;
		}
		
	}
	.v-data-table__wrapper {
    overflow-x: visible !important;
    overflow-y: visible !important;
  }
</style>